import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { FloatingLabelInput } from './FloatingLabelInput';
import { LocationAutocomplete } from './LocationAutocomplete';
import { PrimaryButton } from './PrimaryButton';
import { CmsModal } from './CmsModal';
import { signup, customerApiErrorMessage } from '../../lib/customer-api';
import { setCustomerAuth } from '../../lib/customer-auth';
import { toast } from '../../lib/toast';
import {
  getBookingDraft,
  pendingBookingResumePath,
} from '../../lib/booking-draft';
import { sanitizeName } from '../../lib/validators';

const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const passwordValid = (pw: string) => pw.length >= 8 && /\d/.test(pw);

export function SignUpStep2Form() {
  const navigate = useNavigate();
  const location = useLocation();
  const presetEmail =
    (location.state as { email?: string } | null)?.email ?? '';
  // `from` is set when the customer reached signup via a booking-flow
  // bounce ("Create one" on the sign-in screen). After account creation
  // we resume them on that page so the basket they were building isn't
  // lost. Only honour internal customer paths.
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const safeFrom =
    typeof fromState === 'string' &&
    fromState.startsWith('/') &&
    !fromState.startsWith('//') &&
    !fromState.startsWith('/professional')
      ? fromState
      : null;

  // Step 2 should only be reached via step 1's `navigate(..., { state })`.
  // If someone refreshes or hits the URL directly, send them back so the
  // wizard always starts at step 1 and so the email field can't get
  // accidentally populated by browser autofill from a different account.
  if (!presetEmail) {
    return <Navigate to="/signup/customer" replace />;
  }

  // Pre-fill the address from the one the customer already picked at the
  // start of the booking flow (booking-draft.address), so they don't
  // re-type it when signing up mid-booking (C1). Blank for a direct
  // signup with no inflight booking.
  const draftAddr = getBookingDraft()?.address;
  const [formData, setFormData] = useState({
    email: presetEmail,
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    // Default service address captured at signup. Address line 1 is
    // selected via autocomplete; lat/lng are filled automatically from
    // the selected place and never exposed as editable fields.
    addressLine1: draftAddr?.address_line1 ?? '',
    addressLine2: draftAddr?.address_line2 ?? '',
    // City is auto-filled from the geocode result (postal_town /
    // locality) when the user picks a suggestion, and stays editable
    // afterwards so the user can correct it.
    city: draftAddr?.city ?? '',
    postcode: draftAddr?.postcode ?? '',
    latitude: (draftAddr?.latitude ?? null) as number | null,
    longitude: (draftAddr?.longitude ?? null) as number | null,
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    firstName: false,
    lastName: false,
    password: false,
    confirmPassword: false,
    addressLine1: false,
    city: false,
    postcode: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const newErrors = {
      email: !emailValid(formData.email),
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
      password: !passwordValid(formData.password),
      confirmPassword:
        !formData.confirmPassword ||
        formData.password !== formData.confirmPassword,
      // Address must be picked from Google so we always have coordinates.
      // If lat/lng aren't set the user typed without selecting a suggestion.
      addressLine1:
        !formData.addressLine1.trim() ||
        formData.latitude === null ||
        formData.longitude === null,
      city: !formData.city.trim(),
      postcode: !formData.postcode.trim(),
    };
    setErrors(newErrors);
    const termsMissing = !agreeToTerms;
    setTermsError(termsMissing ? 'You must accept the terms to continue.' : null);
    if (Object.values(newErrors).some(Boolean) || termsMissing) return;

    setSubmitting(true);
    try {
      const res = await signup({
        email: formData.email.trim().toLowerCase(),
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        password: formData.password,
        accept_terms: true,
        address_line1: formData.addressLine1.trim(),
        address_line2: formData.addressLine2.trim() || null,
        city: formData.city.trim(),
        postcode: formData.postcode.trim(),
        latitude: formData.latitude as number,
        longitude: formData.longitude as number,
      });
      setCustomerAuth(res.accessToken, res.user);
      toast.success('Account created successfully!');
      // Resume the booking flow if we got here via /booking → /signin
      // → "Create one". Fall back to the persisted booking draft (so an
      // email-verification round-trip that lost the router state still
      // returns to checkout), then the home page.
      navigate(safeFrom ?? pendingBookingResumePath() ?? '/');
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not create account.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center max-w-[460px] mx-auto">
      <div className="text-center mb-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
          Tell us more about yourself
        </h1>
        <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
          Enter your details to proceed further
        </p>
      </div>

      <div className="w-full flex flex-col gap-6">
        <FloatingLabelInput
          label="Email"
          type="email"
          value={formData.email}
          onChange={(val) => {
            setFormData({ ...formData, email: val });
            setErrors({ ...errors, email: false });
          }}
          icon={``}
          disabled={!!presetEmail}
          className={`w-full ${presetEmail ? 'opacity-70' : ''}`}
          error={errors.email}
          errorMessage="Please enter a valid email address"
        />

        <div className="flex gap-4">
          <FloatingLabelInput
            label="First name *"
            type="text"
            value={formData.firstName}
            onChange={(val) => {
              // Letters only — strip anything else as the user types.
              setFormData({ ...formData, firstName: sanitizeName(val) });
              setErrors({ ...errors, firstName: false });
            }}
            icon={``}
            className="w-1/2"
            error={errors.firstName}
            errorMessage="Required"
          />
          <FloatingLabelInput
            label="Last name *"
            type="text"
            value={formData.lastName}
            onChange={(val) => {
              setFormData({ ...formData, lastName: sanitizeName(val) });
              setErrors({ ...errors, lastName: false });
            }}
            icon={``}
            className="w-1/2"
            error={errors.lastName}
            errorMessage="Required"
          />
        </div>

        <FloatingLabelInput
          label="Password"
          type="password"
          value={formData.password}
          onChange={(val) => {
            setFormData({ ...formData, password: val });
            setErrors({ ...errors, password: false });
          }}
          icon={``}
          className="w-full"
          error={errors.password}
          errorMessage="At least 8 characters and one number."
        />

        <FloatingLabelInput
          label="Confirm password"
          type="password"
          value={formData.confirmPassword}
          onChange={(val) => {
            setFormData({ ...formData, confirmPassword: val });
            setErrors({ ...errors, confirmPassword: false });
          }}
          icon={``}
          className="w-full"
          error={errors.confirmPassword}
          errorMessage={
            !formData.confirmPassword
              ? 'Please confirm your password'
              : 'Passwords do not match'
          }
        />

        {/* Address — used by the booking flow to pre-fill the service
            location. The customer can still override per booking. */}
        <div className="pt-2">
          <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] mb-3">
            Your address
          </p>
          <div className="flex flex-col gap-4">
            <LocationAutocomplete
              label="Address line 1"
              value={formData.addressLine1}
              onChange={(val) => {
                // Typing without picking a suggestion clears the saved
                // coordinates — we won't accept a free-typed address.
                setFormData((prev) => ({
                  ...prev,
                  addressLine1: val,
                  latitude: null,
                  longitude: null,
                }));
                setErrors({ ...errors, addressLine1: false });
              }}
              onPlaceSelected={(place) => {
                setFormData((prev) => ({
                  ...prev,
                  addressLine1: place.description,
                  latitude: place.latitude ?? null,
                  longitude: place.longitude ?? null,
                  // Auto-fill postcode and city if Google returned
                  // them — both stay editable afterwards.
                  postcode: place.postcode ?? prev.postcode,
                  city: place.city ?? prev.city,
                }));
                setErrors((prev) => ({
                  ...prev,
                  addressLine1: false,
                  postcode: place.postcode ? false : prev.postcode,
                  city: place.city ? false : prev.city,
                }));
              }}
              className="w-full"
              containerHeight="h-[70px]"
              icon="location_on"
              error={errors.addressLine1}
              errorMessage="Pick an address from the suggestions"
              placeholder="Start typing your UK address…"
            />
            <FloatingLabelInput
              label="Address line 2 (optional)"
              type="text"
              value={formData.addressLine2}
              onChange={(val) =>
                setFormData({ ...formData, addressLine2: val })
              }
              icon={``}
              className="w-full"
            />
            <div className="grid grid-cols-2 gap-4">
              <FloatingLabelInput
                label="City"
                type="text"
                value={formData.city}
                onChange={(val) => {
                  setFormData({ ...formData, city: val });
                  setErrors({ ...errors, city: false });
                }}
                icon={``}
                className="w-full"
                error={errors.city}
                errorMessage="Required"
              />
              <FloatingLabelInput
                label="Postcode"
                type="text"
                value={formData.postcode}
                onChange={(val) => {
                  setFormData({ ...formData, postcode: val });
                  setErrors({ ...errors, postcode: false });
                }}
                icon={``}
                className="w-full"
                error={errors.postcode}
                errorMessage="Required"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-[10px]">
            <button
              type="button"
              onClick={() => {
                setAgreeToTerms(!agreeToTerms);
                setTermsError(null);
              }}
              className={`size-[20px] rounded-full flex items-center justify-center transition-colors border shrink-0 ${agreeToTerms ? 'bg-[#8e2a2f] border-[#8e2a2f]' : `bg-white ${termsError ? 'border-red-500' : 'border-[#eee]'}`}`}
            >
              {agreeToTerms && (
                <span className="text-white text-[12px]">✓</span>
              )}
            </button>
            <p className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              I agree with{' '}
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                className="text-[#8e2a2f] font-bold underline underline-offset-2 hover:text-[#7a2228] transition-colors"
              >
                terms &amp; conditions
              </button>
            </p>
          </div>
          {termsError && (
            <p className="font-['DM_Sans',sans-serif] text-[12px] text-[#8e2a2f] pl-[30px] font-bold">
              {termsError}
            </p>
          )}
        </div>

        <PrimaryButton
          onClick={handleSubmit}
          className="w-full py-4"
          disabled={submitting}
        >
          {submitting ? 'Creating account…' : 'Continue'}
        </PrimaryButton>
      </div>

      <CmsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
        code="TERMS_CONDITIONS"
        userType="CUSTOMER"
        fallbackTitle="Terms & Conditions"
      />
    </div>
  );
}
